import * as React from "react";
import { useState, useEffect } from "react";
import {
  displayModificationType,
  ModificationType,
  ModifiedApplicationContentType,
  ProcedureDetailType,
} from "@graffer-inc/cloud-gov-core";
import { FormDataConverter, FormSchema } from "@graffer-inc/form-schema";
import { SmartApplyModifyInputRegistry } from "../../../../helpers/form-ext/registry/smart-apply-modify-input-registry";
import { SmartApplyConfirmFieldRegistry } from "../../../../helpers/form-ext/registry/smart-apply-confirm-field-registry";
import { SmartApplyConfirmSectionTemplateRegistry } from "../../../../helpers/form-ext/registry/smart-apply-confirm-section-registry";
import { CharCount } from "../../../../molecules/char-count";
import { ApplicationsConst } from "../../../../../application/domain/application/const";
import {
  ApplicationEntity,
  ModifyInfoType,
} from "../../../../../application/domain/application/application-entity";
import { useModal } from "../../../../../utils/custom-hooks";
import { useModifyForm } from "../../../../helpers/form-ext/hooks/use-modify-form";
import { BaseModal } from "../../../../atoms/base-modal";
import { useConfirmFormEdit } from "../../../../helpers/form-ext/hooks/use-confirm-form-edit";
import { ApiLoadingState } from "../../../../../application/domain/api-loading-state";
import {
  PrimaryButton,
  SecondaryButton,
  Spinner,
} from "@graffer-inc/console-design-kit";
import {
  DefaultConfirmFunctionFieldTemplateRegistry,
  DefaultConfirmRepeatableTemplateRegistry,
  DefaultConfirmStaticFieldTemplateRegistry,
  FormGeneratorV2,
} from "@graffer-inc/form-generator";
import { corporationSearchFormBaseUrl } from "../../../../../utils/corporate-search/base-url";
import { addressSearchBaseUrl } from "../../../../../utils/postal-code/base-url";

type Props = {
  procedure: ProcedureDetailType;
  application: ApplicationEntity;
  modifyingProcess: ApiLoadingState;
  onModify: (
    content: ModifiedApplicationContentType,
    modifyInfo: ModifyInfoType,
  ) => void;
  onModificationEnd: () => void;
};

export const Modification: React.FC<Props> = ({
  procedure,
  application,
  modifyingProcess,
  onModify,
  onModificationEnd,
}) => {
  const modifyTypeModal = useModal(true);
  const discardModal = useModal();
  const [modifyType, setModifyType] = useState<ModificationType>();
  const [comment, setComment] = useState("");
  const isValidInfo =
    modifyType !== undefined &&
    comment.length > 0 &&
    comment.length <= ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH;

  const formProps = useModifyForm({
    application,
    procedure,
    modificationType: modifyType || "ORG_USER_DECISION",
  });
  const formEdit = useConfirmFormEdit({
    schema: formProps.formSchema,
    values: formProps.values,
    errors: formProps.errors,
  });

  const handleModify = async () => {
    // 元に戻す操作の為に残した file, mynumber の中で参照されなくなったものを除去する。
    const schema = FormSchema.fromJSON(procedure.template.formSchema);
    const flattenValues = new FormDataConverter(
      schema,
      formProps.values,
    ).flatten();
    const values = Object.keys(flattenValues).map((k) => flattenValues[k]);
    const files = formProps.fileExchanger
      .getRegistryItems()
      .filter((f) => !f.refKey || values.includes(f.refKey));
    const mynumbers = formProps.myNumberExchanger
      .getRegistryItems()
      .filter((m) => values.includes(m.refKey));
    await onModify(
      {
        jsonDocument: formProps.values,
        files: files,
        myNumbers: mynumbers,
      },
      {
        modificationType: modifyType!,
        internalComment: comment,
      },
    );
  };

  useEffect(() => {
    return onModificationEnd;
  }, [onModificationEnd]);
  useEffect(() => {
    if (modifyingProcess.state === "success") {
      onModificationEnd();
    }
  }, [modifyingProcess.state, onModificationEnd]);

  const isValid =
    isValidInfo &&
    formProps.isValidForm &&
    formProps.isModified &&
    !formEdit.editIds.length &&
    modifyingProcess.state !== "loading";

  const fieldTemplateRegistry = React.useMemo(() => {
    return new SmartApplyConfirmFieldRegistry(procedure.applicantSchemaKeys);
  }, [procedure.applicantSchemaKeys]);

  return (
    <>
      <div className="columns">
        <div className="column">
          <div className="buttons is-right">
            <SecondaryButton
              text={"訂正をやめる"}
              onClick={
                formProps.isModified ? discardModal.open : onModificationEnd
              }
            />
          </div>
        </div>
      </div>
      <div className="columns">
        <div className="column">
          <div className="box">
            <FormGeneratorV2
              {...formProps}
              refkeyContext={{
                fileRefKeyExchanger: formProps.fileExchanger,
                myNumberRefKeyExchanger: formProps.myNumberExchanger,
              }}
              confirmContext={formEdit}
              modificationContext={{
                detector: formProps.detector,
              }}
              submissionContext={formProps.formSubmissionContext}
              InputElementRegistry={new SmartApplyModifyInputRegistry()}
              FieldTemplateRegistry={fieldTemplateRegistry}
              SectionTemplateRegistry={
                new SmartApplyConfirmSectionTemplateRegistry()
              }
              RepeatableTemplateRegistry={
                DefaultConfirmRepeatableTemplateRegistry
              }
              FunctionFieldTemplateRegistry={
                DefaultConfirmFunctionFieldTemplateRegistry
              }
              StaticFieldTemplateRegistry={
                DefaultConfirmStaticFieldTemplateRegistry
              }
              externalServiceUrlConfig={{
                corporationSearchFormBaseUrl: corporationSearchFormBaseUrl,
                addressSearchBaseUrl: addressSearchBaseUrl,
              }}
            />
          </div>
        </div>
        <div className="column">
          <div className="box">
            <div className="field">
              <label className="label">訂正方式</label>
              <div className="control">
                {!!modifyType && (
                  <span className="tag is-medium">
                    {displayModificationType(modifyType)}
                  </span>
                )}
              </div>
            </div>
            <div className="field">
              <label className="label">訂正概要</label>
              <textarea
                name="internalComment"
                className="textarea"
                value={comment}
                rows={8}
                onChange={(e) => setComment(e.target.value)}
                placeholder="訂正概要の記入は必須です。内部管理用のコメントとして記録されます。"
              />
              <CharCount
                text={comment}
                maxLength={ApplicationsConst.INTERNAL_COMMENT_MAX_LENGTH}
              />
            </div>
            {modifyType === "CONFIRMED_TO_APPLICANT" && (
              <div className="notification content">
                <p className="mb-1 has-text-weight-bold">
                  {displayModificationType("CONFIRMED_TO_APPLICANT")}
                  の場合、訂正時に以下の処理が実行されます。
                </p>
                <ul>
                  <li>申請者への訂正通知メールの送信</li>
                </ul>
              </div>
            )}
            {!!application.paymentsV2?.length && (
              <article className="message is-warning">
                <div className="message-body">
                  <p className="mb-1 has-text-weight-bold">
                    決済情報のある申請です。
                  </p>
                  決済金額に影響のある訂正を行った場合、返金や事後決済等の作業が別途必要です。
                </div>
              </article>
            )}
            {modifyingProcess.state === "failure" && (
              <article className="message is-danger">
                <div className="message-body">
                  <p className="mb-1 has-text-weight-bold">
                    訂正処理に失敗しました。
                  </p>
                  {modifyingProcess.errorMessage}
                </div>
              </article>
            )}
            <div className="mt-5">
              <div className={"has-text-centered"}>
                <PrimaryButton
                  text={"訂正を実行する"}
                  disabled={modifyingProcess.state === "loading" || !isValid}
                  onClick={handleModify}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Spinner loading={modifyingProcess.state === "loading"} />
      <BaseModal
        isActive={modifyTypeModal.isActive}
        title="訂正方式の選択"
        onClose={modifyType ? modifyTypeModal.close : onModificationEnd}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            modifyTypeModal.close();
          }}
        >
          <section className="modal-card-body">
            <div className="field">
              <div className={"control"}>
                <Radio
                  mType="ORG_USER_DECISION"
                  isChecked={modifyType === "ORG_USER_DECISION"}
                  description={`職権訂正は内部管理用の申請内容のみ訂正します。訂正内容は申請者には共有されません。`}
                  setValue={setModifyType}
                />
                <Radio
                  mType="CONFIRMED_TO_APPLICANT"
                  isChecked={modifyType === "CONFIRMED_TO_APPLICANT"}
                  description={`申請者確認済み訂正は申請者の控え情報を元に訂正します。過去に職権訂正された内容は引き継がれません。訂正が完了すると申請者に通知が届き、訂正内容が共有されます。`}
                  setValue={setModifyType}
                />
              </div>
            </div>
          </section>
          <footer className="modal-card-foot">
            <button className="button is-fullwidth" onClick={onModificationEnd}>
              訂正をやめる
            </button>
            <button
              type="submit"
              className="button is-info is-fullwidth"
              disabled={!modifyType}
            >
              訂正を開始する
            </button>
          </footer>
        </form>
      </BaseModal>
      <BaseModal
        isActive={discardModal.isActive}
        title="訂正内容の破棄確認"
        onClose={discardModal.close}
      >
        <section className="modal-card-body">
          訂正内容は保存されていません。
          <br />
          訂正内容を破棄しますか？
        </section>
        <footer className="modal-card-foot">
          <button className="button is-fullwidth" onClick={discardModal.close}>
            キャンセル
          </button>
          <button
            className="button is-danger is-outlined is-fullwidth"
            onClick={onModificationEnd}
          >
            破棄する
          </button>
        </footer>
      </BaseModal>
    </>
  );
};

const Radio = (props: {
  mType: ModificationType;
  isChecked: boolean;
  description: string;
  setValue: (mType?: ModificationType) => void;
}) => {
  return (
    <div className="mb-3">
      <label className="radio">
        <div className="columns">
          <div className="column is-narrow">
            <input
              type="radio"
              name="mType"
              value={props.mType}
              onChange={() => props.setValue(props.mType)}
              checked={props.isChecked}
            />
          </div>
          <div className="column">
            <p className="has-text-weight-bold pb-1">
              {displayModificationType(props.mType)}
            </p>
            <p>{props.description}</p>
          </div>
        </div>
      </label>
    </div>
  );
};
